<script setup>
import { ref, computed, watch, onMounted, onBeforeUnmount, inject, nextTick } from 'vue';
import { useChainStateMachine, STATES } from '@/composables/useChainStateMachine';
import { useChainExecution } from '@/composables/useChainExecution';
import { useExecutionPolling } from '@/composables/useExecutionPolling';
import { useManualSelection } from '@/composables/useManualSelection';
import { useDocumentSelection } from '@/composables/useDocumentSelection';
import { useDocumentCreation } from '@/composables/useDocumentCreation';
import { useBulkInput } from '@/composables/useBulkInput';
import { useNotification } from '@/composables/useNotification';
import { isDocumentResult } from '@/utils/documentResultValidator';

import Notification from '@/components/common/SnackBar.vue';
import CRLoading from './CRLoading.vue';
import CRExecutionHistory from './CRExecutionHistory.vue';
import CRMappingError from './CRMappingError.vue';
import CRDocCreationStatus from './CRDocCreationStatus.vue';
import CRBulkInput from './CRBulkInput.vue';
import CRResultsSingle from './CRResultsSingle.vue';
import CRResultsMulti from './CRResultsMulti.vue';

const props = defineProps({
  promptChain: Object,
  node: Object,
  doc: Object,
  isDialog: { type: Boolean, default: false },
  activeGeneration: [String, Number],
  resultLayout: { type: String, default: 'auto' },
  showDocumentActions: { type: Boolean, default: true },
  enableRegeneration: { type: Boolean, default: true },
  enablePreviousExecutions: { type: Boolean, default: true }
});

const emit = defineEmits(['accept', 'statusChange', 'executionStarted', 'openDocument', 'closeDialog', 'stateChange']);

const store = inject('store');
const loginState = inject('loginState');

// --- Compose ---
const { currentState, previousState, transition, forceState } = useChainStateMachine();
const { notification, notify } = useNotification();

const execution = useChainExecution({
  promptChain: () => props.promptChain,
  node: () => props.node,
  doc: () => props.doc,
  store,
  loginState
});

const polling = useExecutionPolling(execution.executionResults, {
  onCompleted: (result, idx, meta) => {
    // Auto-expand and select doc items for completed document results
    if (!meta.isPartialRegen && isDocumentResult(result)) {
      nextTick(() => {
        docSelection.expandAllDocumentItems(result.rowId, result.result, getComponentRef);
        nextTick(() => {
          docSelection.selectAllDocumentItems(result.rowId, result.result, getComponentRef);
        });
      });
    }
    // Update selected result if applicable
    if (execution.selectedResult.value?.rowId === result.rowId) {
      execution.selectedResult.value = result;
    }
    // Check if all results are done
    checkAllResultsDone();
  },
  onFailed: (result, idx) => {
    if (execution.selectedResult.value?.rowId === result.rowId) {
      execution.selectedResult.value = result;
    }
  },
  onAwaitingSelection: (result, selectionData, idx) => {
    if (selectionData) {
      selection.initSelection(selectionData);
    }
    if (execution.selectedResult.value?.rowId === result.rowId) {
      execution.selectedResult.value = result;
    }
  },
  onProgress: (result, idx) => {
    if (execution.selectedResult.value?.rowId === result.rowId) {
      execution.selectedResult.value = result;
    }
  },
  notify
});

const selection = useManualSelection();
const docSelection = useDocumentSelection(execution.executionResults);
const docCreation = useDocumentCreation();
const bulkInput = useBulkInput(execution.manualInputRequirements, execution.levelFrameworkRequirements);

// --- Child component refs ---
const resultsSingleRef = ref(null);
const resultsMultiRef = ref(null);

// --- Helper to get document component refs ---
function getComponentRef(rowId) {
  if (effectiveLayout.value === 'single') {
    return resultsSingleRef.value?.getDocumentRef(rowId) || null;
  }
  return resultsMultiRef.value?.getDocumentRef(rowId) || null;
}

// --- Layout ---
const effectiveLayout = computed(() => {
  if (props.resultLayout !== 'auto') return props.resultLayout;
  return props.promptChain?.trigger_type === 'document_action' ? 'single' : 'multi';
});

// --- Generation status ---
const generationStatus = computed(() => {
  const hasProcessing = execution.executionResults.value.some(r => r.processing);
  const hasAwaitingSelection = execution.executionResults.value.some(r => r.awaitingSelection);

  if (execution.executingBulk.value || hasProcessing || hasAwaitingSelection ||
      execution.resolvingInputs.value || execution.regeneratingPartial.value ||
      docCreation.showDocumentCreationStatus.value) {
    return 'processing';
  }

  const hasCompleted = execution.executionResults.value.some(r => r.success);
  if (hasCompleted && [STATES.RESULTS, STATES.EXECUTING].includes(currentState.value)) {
    return 'complete';
  }

  return 'idle';
});

watch(generationStatus, (newStatus, oldStatus) => {
  if (newStatus !== oldStatus) {
    emit('statusChange', newStatus);
  }
}, { immediate: true });

watch(currentState, (s) => emit('stateChange', s));

// --- State tracking ---
const hasBeenActivated = ref(false);
const selectedPreviousExecution = ref(null);
const applyingDocuments = ref(false);
const applyingBulkDocuments = ref(false);

// --- Check if all results are terminal ---
function checkAllResultsDone() {
  const allDone = execution.executionResults.value.every(r => !r.processing);
  if (allDone && currentState.value === STATES.EXECUTING) {
    forceState(STATES.RESULTS);
  }
}

// --- Initialization ---
async function init() {
  if (execution.resolvingInputs.value) return;

  if (props.doc?.active_generation && !execution.skipActiveGenerationResume.value) {
    forceState(STATES.EXECUTING);
    const resumeResult = await execution.resumeActiveGeneration(
      props.doc.active_generation, polling, notify, docSelection
    );

    if (resumeResult.status === 'completed') {
      forceState(STATES.RESULTS);
      if (isDocumentResult(resumeResult.result)) {
        nextTick(() => {
          docSelection.expandAllDocumentItems(resumeResult.result.rowId, resumeResult.result.result, getComponentRef);
          nextTick(() => {
            docSelection.selectAllDocumentItems(resumeResult.result.rowId, resumeResult.result.result, getComponentRef);
          });
        });
      }
    } else if (resumeResult.status === 'awaiting_selection') {
      forceState(STATES.AWAITING_SELECTION);
      if (resumeResult.selectionData) {
        resumeResult.selectionData.executionId = resumeResult.executionId;
        resumeResult.selectionData.resultIndex = 0;
        selection.initSelection(resumeResult.selectionData);
      }
    } else if (resumeResult.status === 'not_found' || resumeResult.status === 'error') {
      if (props.promptChain?.prompt_chain_id) {
        await resolveAndTransition();
      }
    }
    return;
  }

  if (props.isDialog && !hasBeenActivated.value) {
    return;
  }

  if (props.promptChain?.prompt_chain_id) {
    await resolveAndTransition();
  }
}

async function resolveAndTransition() {
  forceState(STATES.RESOLVING_INPUTS);
  const result = await execution.resolveInputs(props.promptChain.prompt_chain_id, false, notify);

  if (!result.nextState) return;

  switch (result.nextState) {
    case 'EXECUTION_SELECTION':
      forceState(STATES.EXECUTION_SELECTION);
      break;
    case 'MAPPING_ERROR':
      forceState(STATES.MAPPING_ERROR);
      break;
    case 'INPUT_FORM': {
      bulkInput.addBulkRow();
      const autoProceeded = await execution.checkAndAutoProceed(bulkInput, polling, notify, emit);
      if (autoProceeded) {
        forceState(STATES.EXECUTING);
      } else {
        forceState(STATES.INPUT_FORM);
      }
      break;
    }
  }
}

function activate() {
  if (hasBeenActivated.value) return;
  hasBeenActivated.value = true;
  init();
}

// --- Event Handlers ---
async function handleCreateNew() {
  if (execution.resolvingInputs.value || execution.executingBulk.value) return;

  polling.stopAll();
  execution.clearResults();
  execution.skipActiveGenerationResume.value = true;
  selectedPreviousExecution.value = null;
  docSelection.selectedDocumentItems.value = new Map();
  docSelection.selectedResultRows.value = new Set();
  bulkInput.bulkRows.value = [];

  forceState(STATES.RESOLVING_INPUTS);
  const result = await execution.resolveInputs(props.promptChain.prompt_chain_id, true, notify);

  if (!result.nextState) return;

  if (result.nextState === 'MAPPING_ERROR') {
    forceState(STATES.MAPPING_ERROR);
  } else if (result.nextState === 'INPUT_FORM') {
    bulkInput.addBulkRow();
    const autoProceeded = await execution.checkAndAutoProceed(bulkInput, polling, notify, emit);
    if (autoProceeded) {
      forceState(STATES.EXECUTING);
    } else {
      forceState(STATES.INPUT_FORM);
    }
  }
}

async function handleUseExisting(exec) {
  selectedPreviousExecution.value = exec;
  const result = await execution.loadPreviousExecution(exec, notify);

  if (result.success) {
    forceState(STATES.RESULTS);

    // Auto-expand document results
    const firstResult = execution.executionResults.value[0];
    if (firstResult && isDocumentResult(firstResult)) {
      nextTick(() => {
        docSelection.expandAllDocumentItems(firstResult.rowId, firstResult.result, getComponentRef);
      });
    }
  }
}

async function handleExecute() {
  forceState(STATES.EXECUTING);
  await execution.executeBulkGeneration(bulkInput, polling, notify, emit);
}

async function handleSelectionSubmit() {
  const result = await selection.submitSelection(notify);

  if (result && result.success && result.childExecutions.length > 0) {
    // Set up child execution results for polling
    execution.executionResults.value = result.childExecutions.map((childExec, index) => ({
      rowNumber: index + 1,
      rowId: index + 1,
      result: null,
      inputs: {
        manual: childExec.context?.manual_values || {},
        hierarchies: {}
      },
      success: null,
      processing: true,
      executionId: childExec.executionId,
      progress: null,
      currentStep: 'Queued for generation...',
      chainName: null
    }));

    result.childExecutions.forEach((childExec, index) => {
      if (childExec.success && childExec.executionId) {
        polling.startPolling(childExec.executionId, index);
      } else {
        execution.executionResults.value[index].success = false;
        execution.executionResults.value[index].processing = false;
        execution.executionResults.value[index].error = childExec.error || 'Failed to start execution';
      }
    });

    execution.selectedResult.value = execution.executionResults.value[0];
    forceState(STATES.EXECUTING);
  }
}

function handleRegenerate(result, outputKey) {
  execution.regenerateResult(result, outputKey, polling, notify, emit);
}

function handleAccept() {
  const result = execution.executionResults.value[0];
  if (!result || !isDocumentResult(result)) return;

  const selectedItems = docSelection.getSelectedDocumentItems(result.rowId);
  if (selectedItems.size === 0) {
    notify('Please select at least one item to apply', 'warning');
    return;
  }

  applyingDocuments.value = true;
  try {
    const selectedDocumentData = Array.from(selectedItems).map(index => result.result.populatedData[index]);
    const documentUpdateData = {
      populatedData: selectedDocumentData,
      hierarchies: result.result.hierarchies || []
    };
    emit('accept', documentUpdateData);
    docSelection.clearAllDocumentItems(result.rowId, getComponentRef);
  } catch (error) {
    console.error('Error applying document items:', error);
    notify('Error applying document items: ' + error.message, 'error');
  } finally {
    applyingDocuments.value = false;
  }
}

async function handleApplyBulk() {
  applyingBulkDocuments.value = true;
  const success = await docCreation.applySelectedBulkDocumentItems(
    execution.executionResults, docSelection, props.node, props.promptChain, props.doc, notify
  );
  applyingBulkDocuments.value = false;

  if (success) {
    forceState(STATES.DOCUMENT_CREATION_STATUS);
  }
}

function handleBackToInput() {
  forceState(STATES.INPUT_FORM);
  execution.selectedResult.value = null;
}

function handleBackToExecutionSelection() {
  execution.clearResults();
  selectedPreviousExecution.value = null;
  docSelection.selectedDocumentItems.value = new Map();
  docSelection.selectedResultRows.value = new Set();
  forceState(STATES.EXECUTION_SELECTION);
}

function handleDocCreationBack() {
  docCreation.showDocumentCreationStatus.value = false;
  forceState(STATES.RESULTS);
}

// --- Document action helpers ---
function handleExpandAllDoc() {
  const result = execution.selectedResult.value || execution.executionResults.value[0];
  if (result && isDocumentResult(result)) {
    docSelection.expandAllDocumentItems(result.rowId, result.result, getComponentRef);
  }
}

function handleCollapseAllDoc() {
  const result = execution.selectedResult.value || execution.executionResults.value[0];
  if (result) {
    docSelection.collapseAllDocumentItems(result.rowId, getComponentRef);
  }
}

function handleSelectAllDoc() {
  const result = execution.selectedResult.value || execution.executionResults.value[0];
  if (result && isDocumentResult(result)) {
    docSelection.selectAllDocumentItems(result.rowId, result.result, getComponentRef);
  }
}

function handleClearAllDoc() {
  const result = execution.selectedResult.value || execution.executionResults.value[0];
  if (result) {
    docSelection.clearAllDocumentItems(result.rowId, getComponentRef);
  }
}

// --- Expose ---
defineExpose({
  activate,
  resumeActiveGeneration: (id) => {
    forceState(STATES.EXECUTING);
    return execution.resumeActiveGeneration(id, polling, notify, docSelection);
  }
});

// --- Mount ---
onMounted(() => {
  init();
});

onBeforeUnmount(() => {
  execution.stopSessionPolling();
  polling.stopAll();
});
</script>

<template>
  <div class="chain-runner">
    <!-- Loading State -->
    <CRLoading v-if="currentState === STATES.RESOLVING_INPUTS" />

    <!-- Execution Selection -->
    <CRExecutionHistory
      v-else-if="currentState === STATES.EXECUTION_SELECTION"
      :executions="execution.previousExecutions.value"
      :loading="execution.loadingPreviousExecution.value"
      @create-new="handleCreateNew"
      @use-existing="handleUseExisting"
    />

    <!-- Mapping Error -->
    <CRMappingError
      v-else-if="currentState === STATES.MAPPING_ERROR"
      :details="execution.mappingErrorDetails.value"
    />

    <!-- Document Creation Status -->
    <CRDocCreationStatus
      v-else-if="currentState === STATES.DOCUMENT_CREATION_STATUS"
      :requests="docCreation.activeDocumentCreationRequests.value"
      :stats="docCreation.getDocCreationStats.value"
      :get-doc-inputs-fn="docCreation.getDocInputsForRow"
      @back="handleDocCreationBack"
    />

    <!-- Results / Executing / Awaiting Selection -->
    <template v-else-if="[STATES.RESULTS, STATES.AWAITING_SELECTION, STATES.EXECUTING].includes(currentState) && execution.executionResults.value.length > 0">
      <!-- Single Result Layout -->
      <CRResultsSingle
        ref="resultsSingleRef"
        v-if="effectiveLayout === 'single'"
        :result="execution.executionResults.value[0]"
        :is-dialog="isDialog"
        :has-previous-executions="execution.hasPreviousExecutions.value"
        :is-previous-execution="!!selectedPreviousExecution"
        :enable-regeneration="enableRegeneration"
        :show-document-actions="showDocumentActions"
        :available-outputs-for-regen="execution.availableOutputsForRegeneration.value(execution.executionResults.value[0])"
        :regenerating-partial="execution.regeneratingPartial.value"
        :applying-documents="applyingDocuments"
        :selected-doc-count="docSelection.getSelectedDocumentCount(execution.executionResults.value[0]?.rowId)"
        :total-doc-count="isDocumentResult(execution.executionResults.value[0]) ? execution.executionResults.value[0].result?.populatedData?.length || 0 : 0"
        :selection-data="selection.selectionData.value"
        :selected-items="selection.selectedItems.value"
        :edited-items="selection.editedItems.value"
        :new-items="selection.newItems.value"
        :editing-item="selection.editingItem.value"
        :submitting-selection="selection.submittingSelection.value"
        @accept="handleAccept"
        @regenerate="handleRegenerate"
        @back-to-executions="handleBackToExecutionSelection"
        @create-new="handleCreateNew"
        @expand-all="handleExpandAllDoc"
        @collapse-all="handleCollapseAllDoc"
        @select-all-doc="handleSelectAllDoc"
        @clear-all-doc="handleClearAllDoc"
        @doc-selection-changed="docSelection.handleDocumentSelectionChanged"
        @toggle-item="selection.toggleItemSelection"
        @select-all="selection.selectAllItems"
        @clear-all="selection.clearAllSelections"
        @start-edit="selection.startEditingItem"
        @save-edit="selection.saveItemEdit"
        @cancel-edit="selection.cancelItemEdit"
        @add-new="selection.startAddingNewItem"
        @remove-new="selection.removeNewItem"
        @update-field="selection.updateItemField"
        @submit-selection="handleSelectionSubmit"
        @cancel-selection="() => selection.cancelSelection(notify)"
      />

      <!-- Multi Result Layout -->
      <CRResultsMulti
        ref="resultsMultiRef"
        v-else
        :results="execution.executionResults.value"
        :selected-result="execution.selectedResult.value"
        :has-manual-inputs="execution.hasManualInputs.value"
        :total-selected-items="docSelection.totalSelectedItemsAllRows.value"
        :applying-bulk-documents="applyingBulkDocuments"
        :enable-regeneration="enableRegeneration"
        :available-outputs-for-regen="execution.selectedResult.value ? execution.availableOutputsForRegeneration.value(execution.selectedResult.value) : []"
        :regenerating-partial="execution.regeneratingPartial.value"
        :is-dialog="isDialog"
        :is-previous-execution="!!selectedPreviousExecution"
        :get-selected-doc-count-fn="docSelection.getSelectedDocumentCount"
        :get-row-checkbox-state-fn="docSelection.getRowCheckboxState"
        :selection-data="selection.selectionData.value"
        :selected-items="selection.selectedItems.value"
        :edited-items="selection.editedItems.value"
        :new-items="selection.newItems.value"
        :editing-item="selection.editingItem.value"
        :submitting-selection="selection.submittingSelection.value"
        @select-result="execution.selectResult"
        @back-to-input="handleBackToInput"
        @apply-bulk="handleApplyBulk"
        @select-all-rows="() => docSelection.selectAllInAllRows(getComponentRef)"
        @clear-all-rows="() => docSelection.clearAllInAllRows(getComponentRef)"
        @row-checkbox-click="(rowId) => docSelection.handleRowCheckboxClick(rowId, getComponentRef)"
        @regenerate="handleRegenerate"
        @back-to-results="() => { execution.selectedResult.value = null; }"
        @toggle-item="selection.toggleItemSelection"
        @select-all="selection.selectAllItems"
        @clear-all="selection.clearAllSelections"
        @start-edit="selection.startEditingItem"
        @save-edit="selection.saveItemEdit"
        @cancel-edit="selection.cancelItemEdit"
        @add-new="selection.startAddingNewItem"
        @remove-new="selection.removeNewItem"
        @update-field="selection.updateItemField"
        @submit-selection="handleSelectionSubmit"
        @cancel-selection="() => selection.cancelSelection(notify)"
        @expand-all-doc="handleExpandAllDoc"
        @collapse-all-doc="handleCollapseAllDoc"
        @select-all-doc="handleSelectAllDoc"
        @clear-all-doc="handleClearAllDoc"
        @doc-selection-changed="docSelection.handleDocumentSelectionChanged"
      />
    </template>

    <!-- Bulk Input Form -->
    <CRBulkInput
      v-else-if="currentState === STATES.INPUT_FORM"
      :bulk-rows="bulkInput.bulkRows.value"
      :manual-input-requirements="execution.manualInputRequirements.value"
      :level-framework-requirements="execution.levelFrameworkRequirements.value"
      :executing="execution.executingBulk.value"
      :can-execute="bulkInput.canExecuteBulk.value"
      :total-execution-count="bulkInput.totalExecutionCount.value"
      :execution-mode="execution.executionMode.value"
      :context-mode="execution.contextMode.value"
      :max-context-rows="execution.maxContextRows.value"
      :context-output-keys="execution.contextOutputKeys.value"
      :chain-output-keys="execution.chainOutputKeys.value"
      @add-row="bulkInput.addBulkRow"
      @remove-row="bulkInput.removeBulkRow"
      @execute="handleExecute"
      @update:executionMode="execution.executionMode.value = $event"
      @update:contextMode="execution.contextMode.value = $event"
      @update:maxContextRows="execution.maxContextRows.value = $event"
      @update:contextOutputKeys="execution.contextOutputKeys.value = $event"
    />

    <!-- Notification -->
    <Notification
      v-if="notification.text"
      :text="notification.text"
      :type="notification.type"
    />
  </div>
</template>

<style scoped>
.chain-runner {
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  max-height: calc(100vh - 150px);
}
</style>

<style lang="scss">
/* ==========================================================================
   Chain Runner: Base Layout Styles (light mode)
   Unscoped so child component classes are styled correctly
   ========================================================================== */
.chain-runner {
  /* Streamlined result output - flex container for document items */
  .streamlined-result-output {
    flex: 1;
    display: flex;
    flex-direction: column;
    min-height: 0;
  }

  /* Document result container */
  .document-result {
    display: flex;
    flex-direction: column;
    border: 1px solid #e0e0e0;
    overflow: hidden;
    max-height: calc(100vh - 120px);
  }

  /* Document actions header bar */
  .document-actions-header {
    padding: 12px;
    border-bottom: 1px solid #e0e0e0;
    display: flex;
    justify-content: space-between;
    align-items: center;
    flex-wrap: wrap;
    gap: 12px;
  }

  /* Document list containers */
  .document-items-list {
    flex: 1;
    overflow-y: auto;
    min-height: 0;
    max-height: calc(100vh - 205px);
  }

  .document-items-list-dialog {
    flex: 1;
    overflow-y: auto;
    min-height: 0;
    max-height: calc(100vh - 400px);
  }
}

/* ==========================================================================
   Chain Runner: Additional Outputs Expansion Panel
   Needs ::v-deep from orchestrator level for Vuetify panel overrides
   ========================================================================== */
.chain-runner {
  .additional-outputs-expansion {
    box-shadow: none;
  }

  ::v-deep .additional-outputs-panel {
    border-radius: 6px;
    overflow: hidden;
  }

  ::v-deep .additional-outputs-header {
    padding: 12px 16px !important;
    font-weight: 600;
    font-size: 14px;
    min-height: 44px;
  }

  ::v-deep .additional-outputs-content .v-expansion-panel-content__wrap {
    padding: 0;
  }

  /* Light mode additional outputs */
  .additional-outputs-expansion.theme--light {
    ::v-deep .additional-outputs-panel {
      background: #ffffff;
      border: 1px solid #e2e8f0;
    }
    ::v-deep .additional-outputs-header {
      background: #ffffff;
      color: #1e293b;
      border-bottom: 1px solid #e2e8f0;
      &::before { background-color: #f7f9fb; opacity: 0; }
      &:hover { background: #f7f9fb; }
    }
    ::v-deep .v-expansion-panel--active .additional-outputs-header {
      background: #f7f9fb;
    }
    ::v-deep .additional-outputs-content {
      background: #f7f9fb;
    }
    ::v-deep .additional-outputs-content .v-expansion-panel-content__wrap {
      background: #f7f9fb;
    }
  }

  /* Dark mode additional outputs */
  .additional-outputs-expansion.theme--dark {
    ::v-deep .additional-outputs-panel {
      background: #272733 !important;
      border: 1px solid #3d3d50;
    }
    ::v-deep .additional-outputs-header {
      background: #272733 !important;
      color: #e2e8f0 !important;
      border-bottom: 1px solid #3d3d50;
      &::before { background-color: #272733 !important; opacity: 0 !important; }
      &:hover { background: #1e1e2e !important; }
    }
    ::v-deep .v-expansion-panel--active .additional-outputs-header {
      background: #1e1e2e !important;
    }
    ::v-deep .additional-outputs-content {
      background: #1e1e2e !important;
    }
    ::v-deep .additional-outputs-content .v-expansion-panel-content__wrap {
      background: #1e1e2e !important;
      color: #e2e8f0;
    }
  }
}

/* ==========================================================================
   Chain Runner: Dark Mode Overrides
   Unscoped so it can target child component classes via descendant selectors.
   Scoped to .chain-runner to prevent leaking.
   ========================================================================== */
.v-application.theme--dark .chain-runner {

  /* --- Document Actions & Items --- */
  .document-actions-header {
    background: #272626;
    color: #cbcbcb !important;
  }
  .document-item {
    background: #272626;
    color: #cbcbcb !important;
  }
  .document-item.selected {
    border: 2px solid #4caf50;
    box-shadow: 0 2px 8px rgba(76, 175, 80, 0.15) !important;
    background: #272626;
    color: #cbcbcb !important;
  }

  /* --- Bulk Input --- */
  .bulk-input-container {
    background: linear-gradient(135deg, #1e1e2e 0%, #2d2d3d 50%, #1e1e2e 100%);
    border-color: rgba(100, 116, 139, 0.3);
  }
  .bulk-input-header {
    background: linear-gradient(135deg, #3b5998 0%, #6b5b95 50%, #5b5ea6 100%);
  }
  .bulk-table-card {
    background: #272733;
    border-color: rgba(100, 116, 139, 0.2);
  }
  .bulk-input-table {
    background: #272733;
  }
  .bulk-input-table thead th {
    background: linear-gradient(180deg, #1e1e2e 0%, #252535 100%);
    border-color: rgba(100, 116, 139, 0.2);
  }
  .column-header-label { color: #cbd5e1; }
  .column-header-icon { color: #94a3b8; }
  .bulk-input-table tbody td {
    border-color: rgba(100, 116, 139, 0.15);
  }
  .bulk-input-table tbody tr.bulk-row:hover {
    background: linear-gradient(90deg, #2d2d3d 0%, #352d4d 100%);
  }
  .row-number-badge {
    background: linear-gradient(135deg, #3730a3 0%, #4f46e5 100%);
    color: #e0e7ff;
  }
  .bulk-text-field,
  .bulk-select-field {
    ::v-deep .v-input__slot { background: #1e1e2e !important; }
    ::v-deep .v-input--is-focused .v-input__slot { background: #252535 !important; }
  }
  .delete-row-btn {
    color: #64748b !important;
    &:hover:not(:disabled) {
      color: #f87171 !important;
      background: rgba(239, 68, 68, 0.15) !important;
    }
  }
  .bulk-actions-footer {
    background: linear-gradient(180deg, #1e1e2e 0%, #252535 100%);
    border-color: rgba(100, 116, 139, 0.2);
  }
  .bulk-stats { color: #94a3b8; }
  .execution-multiplier {
    background: linear-gradient(135deg, #3f1d38 0%, #4c1d3d 100%);
    border-color: rgba(236, 72, 153, 0.3);
  }
  .execution-count { color: #f472b6; }

  /* --- Results Container & Split View --- */
  .results-header {
    background: linear-gradient(135deg, #5b21b6 0%, #7c3aed 50%, #6d28d9 100%);
  }
  .results-split-view { background: #1e1e2e; }
  .results-list-panel {
    background: linear-gradient(180deg, #252535 0%, #1e1e2e 100%);
    border-color: rgba(100, 116, 139, 0.2);
  }
  .results-list-header {
    background: #1e1e2e;
    border-color: rgba(100, 116, 139, 0.2);
    color: #94a3b8;
  }
  .results-count-badge {
    background: linear-gradient(135deg, #3730a3 0%, #4f46e5 100%);
    color: #e0e7ff;
  }
  .result-item {
    background: #272733;
    border-color: transparent;
    &:hover {
      background: #2d2d3d;
      border-color: rgba(139, 92, 246, 0.3);
    }
    &.active {
      background: linear-gradient(135deg, #312e81 0%, #3b1d5d 100%);
      border-color: #8b5cf6;
    }
  }
  .result-item-title { color: #e2e8f0; }
  .chain-name { color: #94a3b8; }
  .result-progress-bar { background: rgba(100, 116, 139, 0.3); }
  .step-info { color: #64748b; }
  .result-item-arrow { color: #475569; }
  .result-detail-panel { background: #1e1e2e; }

  /* --- Detail Header Card --- */
  .detail-header-card {
    background: linear-gradient(135deg, #1e1e2e 0%, #252535 100%);
    border-color: rgba(100, 116, 139, 0.3);
  }
  .detail-header-top {
    background: linear-gradient(135deg, #5E2872 0%, #5E2872 50%, #8b549f 100%);
  }
  .detail-header-params {
    background: #272733;
    border-color: rgba(100, 116, 139, 0.2);
  }
  .param-tag {
    background: linear-gradient(135deg, #1e1e2e 0%, #252535 100%);
    border-color: rgba(100, 116, 139, 0.4);
  }
  .param-label { color: #94a3b8; }
  .param-value { color: #e2e8f0; }
  .param-tag.hierarchy {
    background: linear-gradient(135deg, #312e81 0%, #3b1d5d 100%);
    border-color: rgba(139, 92, 246, 0.4);
    .param-value { color: #c4b5fd; }
  }
  .detail-header-progress {
    background: linear-gradient(135deg, #1e293b 0%, #1e1e2e 100%);
  }
  .progress-status-row .current-step { color: #93c5fd; }
  .header-progress-bar { background: rgba(59, 130, 246, 0.2); }
  .detail-title { color: #e2e8f0; }
  .chain-name-header { color: #94a3b8; }
  .stat-value { color: #e2e8f0; }
  .stat-label { color: #ffffff; }

  /* --- Detail Section --- */
  .detail-section {
    background: #272733;
    border-color: rgba(100, 116, 139, 0.3);
  }
  .section-title {
    color: #e2e8f0;
    background: #1e1e2e;
    border-color: #3d3d50;
    .v-icon { color: #94a3b8 !important; }
  }
  .section-content {
    background: #272733 !important;
    color: #e2e8f0;
  }

  /* --- Selection Section --- */
  .selection-section {
    .selection-items-container {
      background: #1e1e2e;
      border-color: rgba(100, 116, 139, 0.3);
    }
    .selection-item {
      background: #272733;
      border-color: rgba(100, 116, 139, 0.2);
      &:hover { background: #2d2d3d; }
      &.selected {
        background: linear-gradient(135deg, #1e3a5f 0%, #1e2d4d 100%);
        border-left-color: #3b82f6;
      }
      &.editing {
        background: linear-gradient(135deg, #3d3520 0%, #2d2a1e 100%);
        border-left-color: #f59e0b;
      }
      &.new-item {
        background: linear-gradient(135deg, #1a3d2e 0%, #1e2d25 100%);
        border-left-color: #10b981;
      }
    }
    .selection-item-field {
      strong { color: #94a3b8; }
      span { color: #e2e8f0; }
    }
    .selection-item-json {
      background: #1e1e2e;
      color: #e2e8f0;
    }
    .add-item-container {
      background: #1e1e2e;
      border-color: rgba(100, 116, 139, 0.3);
    }
  }

  /* --- Result Output --- */
  .result-output {
    background: linear-gradient(135deg, #1e1e2e 0%, #252535 100%) !important;
    color: #e2e8f0 !important;
    border-color: rgba(100, 116, 139, 0.3) !important;
  }
  .text-result-streamlined {
    background: #1e1e2e !important;
    color: #e2e8f0 !important;
  }

  /* --- Document Result --- */
  .document-result {
    border-color: rgba(100, 116, 139, 0.3) !important;
    background: #272733 !important;
  }
  .document-selection-info { color: #94a3b8 !important; }
  .document-actions .v-btn {
    background: #272733 !important;
    color: #e2e8f0 !important;
    border-color: rgba(100, 116, 139, 0.3) !important;
    &:hover { background: #2d2d3d !important; }
  }
  .document-items-list,
  .document-items-list-dialog {
    background: #1e1e2e !important;
  }

  /* Deep selectors for GenerateContentDoc child component */
  ::v-deep .document-items-expansion { background: #1e1e2e !important; }
  ::v-deep .document-item-panel {
    background: #272733 !important;
    border-color: #3d3d50 !important;
  }
  ::v-deep .document-item-header {
    background: #272733 !important;
    color: #e2e8f0 !important;
  }
  ::v-deep .document-item-content { background: #1e1e2e !important; }
  ::v-deep .content-holder {
    background: #1e1e2e !important;
    color: #e2e8f0 !important;

    /* Regenerating overlay - dark mode */
    &.regenerating {
      &::after {
        background: linear-gradient(135deg, rgba(30, 30, 46, 0.90) 0%, rgba(39, 39, 51, 0.90) 100%) !important;
        color: #e2e8f0 !important;
      }
    }
  }

  /* Content processing overlay - dark mode */
  ::v-deep .content-processing-overlay {
    background: linear-gradient(135deg, rgba(30, 30, 46, 0.90) 0%, rgba(39, 39, 51, 0.90) 100%) !important;
    border-color: rgba(99, 102, 241, 0.3) !important;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3) !important;

    .processing-content .processing-text {
      color: #e2e8f0 !important;
    }
  }

  ::v-deep .document-values {
    background: #272733 !important;
    color: #e2e8f0 !important;
  }
  ::v-deep .single-value { color: #e2e8f0 !important; }
  ::v-deep .values-list,
  ::v-deep .value-item { color: #e2e8f0 !important; }
  ::v-deep .child-values-nested-list,
  ::v-deep .child-value-nested-item { color: #cbd5e1 !important; }
  ::v-deep .document-part-name { color: #e2e8f0 !important; }
  ::v-deep .chevron-icon { color: #64748b !important; }
  ::v-deep .v-expansion-panel--active .chevron-icon { color: #e2e8f0 !important; }
  ::v-deep .justification-header {
    background: #2a2a3d !important;
    color: #64b5f6 !important;
  }
  ::v-deep .justification-content { background: #2a2a3d !important; }
  ::v-deep .justification-content .v-expansion-panel-content__wrap {
    background: #2a2a3d !important;
    color: #cbd5e1 !important;
  }

  /* Vuetify expansion panel dark overrides */
  ::v-deep .v-expansion-panel { background: #272733 !important; border: 1px solid #3d3d50; }
  ::v-deep .v-expansion-panel-header {
    background: #272733 !important;
    color: #e2e8f0 !important;
    border-bottom: 1px solid #3d3d50;
  }
  ::v-deep .v-expansion-panel--active .v-expansion-panel-header { background: #1e1e2e !important; }
  ::v-deep .v-expansion-panel-header:hover { background: #1e1e2e !important; }
  ::v-deep .v-expansion-panel-header::before { background-color: #272733 !important; opacity: 0 !important; }
  ::v-deep .v-expansion-panel--active > .v-expansion-panel-header::before,
  ::v-deep .v-expansion-panel-header:hover::before {
    background-color: #1e1e2e !important;
    opacity: 0.1 !important;
  }
  ::v-deep .v-expansion-panel-content__wrap {
    background: #1e1e2e !important;
    color: #e2e8f0;
  }
  ::v-deep .v-expansion-panel-header .v-ripple__container,
  ::v-deep .v-expansion-panel-header .v-ripple__animation {
    background: #1e1e2e !important;
    opacity: 0.2 !important;
  }

  /* Document Creation Status dark overrides */
  .document-creation-header { background: #5e35b1; }
  .document-creation-content { background: #1e1e2e; }
  .doc-creation-batch {
    border-color: #3d3d50;
  }
  .batch-header {
    background: #272733;
    border-color: #3d3d50;
  }
  .batch-title { color: #e2e8f0; }
  .doc-creation-card {
    background: #272733;
    border-color: #3d3d50;
    &.complete { border-color: #4CAF50; background: #1a3d2e; }
    &.error { border-color: #f44336; background: #3d1a1a; }
  }
  .card-title { color: #e2e8f0; }
  .card-subtitle { color: #94a3b8; }
  .batch-summary {
    background: #1e1e2e;
    border-color: #3d3d50;
  }
  .summary-text { color: #94a3b8; }

  /* Input table dark */
  .input-table td {
    border-color: rgba(100, 116, 139, 0.2);
    color: #e2e8f0;
    &:first-child { color: #94a3b8; }
  }

  /* Output value cell */
  .output-value-cell { color: #cbd5e1; }
}

/* Shared animation keyframes */
@keyframes shimmer {
  0% { transform: translateX(-100%); }
  100% { transform: translateX(100%); }
}

@keyframes pulse {
  0%, 100% { opacity: 0.4; transform: scale(1); }
  50% { opacity: 0.7; transform: scale(1.05); }
}
</style>
